import { useQuery } from '@tanstack/react-query';
import config from 'api/config';
import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import axios from 'axios';
import { CASTFile, CASTFileStatus } from 'types/models/CASTFile';

import getConfig from 'next/config';
import useScanInProgressStore from 'stores/scanInProgressFilesStore';
import { wait } from 'utils/helpers';

const { publicRuntimeConfig } = getConfig();

export class FileService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async uploadCASTFile(file: CASTFile): Promise<CASTFile['id']> {
    const { data } = await this.publicHttpClient.post<CASTFile['id']>(`/file`, file, {
      withCredentials: false,
    });
    return data;
  }

  async getCASTFile(fileId: string): Promise<CASTFile> {
    const { data } = await this.publicHttpClient.get<CASTFile>(`/file/${fileId}`, {
      withCredentials: false,
    });
    return data;
  }

  async downloadFile(fileId: string) {
    const { data } = await this.publicHttpClient.get<Blob>(`/file/${fileId}/download`, {
      withCredentials: false,
      responseType: 'blob',
    });

    const blob = new Blob([data], { type: data.type });
    return URL.createObjectURL(blob);
  }

  getCachedCASTFile(file: string | CASTFile) {
    const fileId = typeof file === 'string' ? file : file?.id;
    return useQuery(['DownloadFile', fileId], () => this.getFileUsingBucketLocation(fileId), {
      enabled: !!fileId,
      cacheTime: config.cacheTime,
      staleTime: config.cacheTime,
      meta: {
        errorMessage: 'An error occurred downloading the file.',
      },
    });
  }

  getFileUsingBucketLocation(fileId: string): Promise<CASTFile>;
  getFileUsingBucketLocation(file: CASTFile): Promise<CASTFile>;
  async getFileUsingBucketLocation(fileOrFileId: CASTFile | string): Promise<CASTFile> {
    let file: CASTFile;
    if (typeof fileOrFileId === 'string') {
      file = await this.getCASTFile(fileOrFileId);
    } else {
      file = fileOrFileId;
    }
    if (!file.status) file.status = CASTFileStatus.LOADING;
    const scanInProgress = useScanInProgressStore.getState().isFileScanInProgress(file);

    try {
      file.url = await this.downloadFile(file.id);

      file.status = file.url ? CASTFileStatus.SUCCESS : CASTFileStatus.ERROR;
      // Remove file from scan in progress list
      if (scanInProgress) {
        useScanInProgressStore.getState().removeFileFromScanInProgressList(file);
      }

      return file;
    } catch (e) {
      // if error is 404, check if file is still uploading, reload if so
      if (e.response?.status === 404 && scanInProgress) {
        await wait(3000);
        file.status = CASTFileStatus.SCANNING;
        return this.getFileUsingBucketLocation(file);
      }

      console.error('Error getting file using bucket location', e);
      file.status = CASTFileStatus.ERROR;
      return file;
    }
  }

  async uploadFileUsingBucketLocation(file: File, bucketLocation: string): Promise<boolean> {
    try {
      // Make API call to get signed url for uploading a file
      const { data } = await axios(
        `${publicRuntimeConfig.basePath}/api/signedUrl/upload?filePath=${encodeURIComponent(
          bucketLocation
        )}`,
        {
          method: 'GET',
        }
      );

      // Make API call using signed url
      const putResponse = await axios.put(data?.data, file, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });
      if (putResponse.status === 200) {
        return true;
      } else {
        throw Error('Error uploading file to bucket');
      }
    } catch (e) {
      console.error('Error uploading file to bucket', e);
      return false;
    }
  }
}
